import { IAssetCategoryResponse } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { AssetCategoryDetailTreeNode } from 'pages/Settings/AssetType/AssetCategoryDetailTreeNode';
import {
    CategoryCreationInfo,
    CreateAssetCategoryModal,
} from 'pages/Settings/AssetType/CreateAssetCategoryModal';
import {
    CategoryEditInfo,
    EditAssetCategoryModal,
} from 'pages/Settings/AssetType/EditAssetCategoryModal';
import {
    CategoryMoveInfo,
    MoveAssetCategoryConfirmationModal,
} from 'pages/Settings/AssetType/MoveAssetCategoryConfirmationModal';
import { CategoryTreeData } from 'pages/Settings/AssetType/types';
import { useState } from 'react';
import { DataTree } from 'shared/data_tree/DataTree';
import {
    Tree,
    TreeExpansionChangeEvent,
    TreeNode,
    TreeNodeMoveInfo,
    TreeNodePosition,
    TreePreviewRenderInfo,
} from 'shared/data_tree/types';
import { excludeNullAndUndefined } from 'utils/array';
import { getChildCategories, GetChildCategoriesOptions } from 'utils/asset';

const canMoveTo = (
    allCategories: IAssetCategoryResponse[],
    moveType: TreeNodePosition,
    node: TreeNode<CategoryTreeData>,
    targetNode: TreeNode<CategoryTreeData>
) =>
    // Must be moving "to" a target (not "before" or "after" a target)
    moveType === 'child' &&
    // Cannot move to a child category
    targetNode.parent?.data.category?.Id !== node.data.category?.Id &&
    // Cannot move resulting in sibling categories with the same name
    getChildCategories({
        allCategories,
        parentCategoryId: targetNode.data.category?.Id,
    }).every((child) => child.ShortName !== node.data.category?.ShortName);

const renderPreview = (info: TreePreviewRenderInfo<CategoryTreeData>) =>
    info.node.data.category?.ShortName ?? '';

const getCategoryShortNames = (options: GetChildCategoriesOptions) =>
    excludeNullAndUndefined(
        getChildCategories(options).map(({ ShortName }) => ShortName)
    );

export type AssetCategoryDetailTreeProps = Readonly<{
    assetTypeId: number;
    allCategories: IAssetCategoryResponse[];
    filterText: string;
    categoryTree: Tree<CategoryTreeData>;
    selectedCategory: IAssetCategoryResponse | undefined;
    expandedCategoryIds: string[];
    onCategorySelect: (category: IAssetCategoryResponse | undefined) => void;
    onExpandChange: (event: TreeExpansionChangeEvent<CategoryTreeData>) => void;
    onCategoryCreate: (category: IAssetCategoryResponse) => void;
    onCategoryUpdate: (category: IAssetCategoryResponse) => void;
    onCategoryDelete: () => void;
    className?: string;
}>;

export function AssetCategoryDetailTree({
    assetTypeId,
    allCategories,
    filterText,
    categoryTree,
    selectedCategory,
    expandedCategoryIds,
    onCategorySelect,
    onExpandChange,
    onCategoryCreate,
    onCategoryUpdate,
    onCategoryDelete,
    className,
}: AssetCategoryDetailTreeProps) {
    const { showToast } = useNotificationContext();
    const { canEditAssetType } = usePermissions();

    const [createInfo, setCreateInfo] = useState<CategoryCreationInfo>();
    const [editInfo, setEditInfo] = useState<CategoryEditInfo>();
    const [deleteInfo, setDeleteInfo] = useState<IAssetCategoryResponse>();
    const [moveInfo, setMoveInfo] = useState<CategoryMoveInfo>();

    const { mutate: deleteCategory, isPending: isDeleting } = useMutation({
        mutationFn: async () => {
            if (!deleteInfo) {
                throw new Error('Expected category');
            }
            return sdClient.deleteAssetClassificationCategory({
                categoryId: deleteInfo.Id,
            });
        },

        onSuccess: () => {
            setDeleteInfo(undefined);
            onCategoryDelete();
            showToast({
                type: 'success',
                content: 'Successfully deleted asset category',
            });
        },

        onError: () =>
            showToast({ type: 'error', content: 'Failed to delete asset category' }),
    });

    const onCategoryCreateClick = (node: TreeNode<CategoryTreeData>) => {
        setCreateInfo({
            parentCategory: node.data.category,
            siblingShortNames: getCategoryShortNames({
                allCategories,
                parentCategoryId: node.data.category?.Id,
            }),
        });
    };

    const onCategoryEditClick = (node: TreeNode<CategoryTreeData>) => {
        if (node.data.category) {
            setEditInfo({
                category: node.data.category,
                siblingShortNames: getCategoryShortNames({
                    allCategories,
                    parentCategoryId:
                        node.data.category.ParentCategory?.AssetCategoryParentId,
                    excludeCategory: node.data.category,
                }),
            });
        }
    };

    const onCategoryDeleteClick = (node: TreeNode<CategoryTreeData>) => {
        if (node.data.category) {
            setDeleteInfo(node.data.category);
        }
    };

    const onMoveInitiated = ({ nodeId, targetNodeId }: TreeNodeMoveInfo) => {
        const { category } = categoryTree.get(nodeId).data;

        if (category) {
            setMoveInfo({
                category,
                parentCategory: categoryTree.get(targetNodeId).data.category,
            });
        }
    };

    const onCreateModalClose = (createdCategory: IAssetCategoryResponse | undefined) => {
        setCreateInfo(undefined);
        if (createdCategory) {
            onCategoryCreate(createdCategory);
        }
    };

    const onEditModalClose = (updatedCategory: IAssetCategoryResponse | undefined) => {
        setEditInfo(undefined);
        if (updatedCategory) {
            onCategoryUpdate(updatedCategory);
        }
    };

    const onMoveModalClose = (updatedCategory: IAssetCategoryResponse | undefined) => {
        setMoveInfo(undefined);
        if (updatedCategory) {
            onCategoryUpdate(updatedCategory);
        }
    };

    return (
        <div className={className}>
            <DataTree<CategoryTreeData>
                treeId="asset-category-tree"
                data={categoryTree}
                selectable={{
                    isSelected: (node) => selectedCategory?.Id.toString() === node.id,
                    selectionStyle: 'custom',
                    onChange: (event) => onCategorySelect(event.node.data.category),
                }}
                expandable={{
                    onChange: onExpandChange,
                    isExpanded: (node) => expandedCategoryIds.includes(node.id),
                }}
                movable={
                    canEditAssetType
                        ? {
                              canMoveTo: ({ moveType, nodeId, targetNodeId }) =>
                                  canMoveTo(
                                      allCategories,
                                      moveType,
                                      categoryTree.get(nodeId),
                                      categoryTree.get(targetNodeId)
                                  ),
                              renderPreview,
                              onMove: onMoveInitiated,
                          }
                        : undefined
                }
                renderNode={({ node }) => (
                    <AssetCategoryDetailTreeNode
                        node={node}
                        selectedCategory={selectedCategory}
                        filterText={filterText}
                        onCategoryCreateClick={() => onCategoryCreateClick(node)}
                        onCategoryEditClick={() => onCategoryEditClick(node)}
                        onCategoryDeleteClick={() => onCategoryDeleteClick(node)}
                    />
                )}
            />
            {moveInfo && (
                <MoveAssetCategoryConfirmationModal
                    moveInfo={moveInfo}
                    onClose={onMoveModalClose}
                />
            )}
            {createInfo && (
                <CreateAssetCategoryModal
                    assetTypeId={assetTypeId}
                    {...createInfo}
                    onClose={onCreateModalClose}
                />
            )}
            {editInfo && (
                <EditAssetCategoryModal {...editInfo} onClose={onEditModalClose} />
            )}
            {deleteInfo && (
                <ConfirmModal
                    title={`Delete '${deleteInfo.ShortName}'`}
                    actionText="delete this category? Other users in your organization may be referencing it."
                    checkText="delete this category"
                    actionButtonText="Delete"
                    closable={!isDeleting}
                    actionable={!isDeleting}
                    onAction={deleteCategory}
                    onClose={() => setDeleteInfo(undefined)}
                />
            )}
        </div>
    );
}
